import React, { useState, useMemo } from "react";
import { BarChart, Bar, Cell, XAxis, YAxis, ReferenceLine, ResponsiveContainer, LabelList } from "recharts";
// Logo oficial Edge Elite (wordmark + pelota naranja) — mismo que landing/sidebar
import { EELogo as OfficialEELogo } from "../EEBrand";
import { useTrendsToday, useTrendSubject, adaptSubjectFromBackend } from "../../hooks/useTrendsData";

/* ============================================================================
   Edge Elite — Oportunidades (Hub) + Explorador de tendencias
   - Pantalla 1: HUB que promociona partidos del día + oportunidades detectadas
   - Pantalla 2: DETALLE tipo widget (réplica modernizada de las fotos)
   - Logos oficiales ESPN (con fallback a monograma)
   - Marca de agua Edge Elite detrás del header
   - Datos reales embebidos · logos oficiales ESPN
   ========================================================================== */

const ORANGE = "#FF6B2C", ORANGE_DK = "#BA3600";
const THEMES = {
  light: {
    bg: "#F5F5F7", card: "#FFFFFF", orange: ORANGE, orangeDark: ORANGE_DK,
    pos: "#16A34A", posDk: "#15803D", neg: "#DC2626", negDk: "#B91C1C",
    blue: "#1E40AF", text: "#1D1D1F", muted: "#6E6E73", line: "#ECECEF",
    chip: "#1D1D1F", track: "#E3E3E8", seg: "#EFEFF2", wmShadow: "0 1px 0 rgba(255,255,255,.55)",
  },
  dark: {
    bg: "#0F1012", card: "#1A1B1F", orange: ORANGE, orangeDark: ORANGE_DK,
    pos: "#22C55E", posDk: "#15803D", neg: "#EF4444", negDk: "#B91C1C",
    blue: "#3B82F6", text: "#F2F2F5", muted: "#9A9AA0", line: "#2A2B30",
    chip: "#000000", track: "#2A2B30", seg: "#26272C", wmShadow: "0 1px 0 rgba(0,0,0,.5)",
  },
};

/* ---------- DATOS REALES ESPN (más reciente primero) ---------- */
const SUBJECTS = {
  okc: {
    id: "okc", type: "team", name: "Oklahoma City Thunder", short: "Thunder",
    abbr: "okc", league: "nba", nextOpp: "den", nextHA: "vs", nextTime: "Hoy 21:00 hrs (Chile)",
    games: [
      { label: "Dic 18", opp: "lac", ha: "vs", res: "W", H1: 64, total: 122, oppScore: 101 },
      { label: "Dic 13", opp: "sa", ha: "vs", res: "L", H1: 49, total: 109, oppScore: 111 },
      { label: "Dic 10", opp: "phx", ha: "vs", res: "W", H1: 74, total: 138, oppScore: 89 },
      { label: "Dic 07", opp: "utah", ha: "@", res: "W", H1: 74, total: 131, oppScore: 101 },
      { label: "Dic 05", opp: "dal", ha: "vs", res: "W", H1: 63, total: 132, oppScore: 111 },
      { label: "Dic 02", opp: "gs", ha: "@", res: "W", H1: 63, total: 124, oppScore: 112 },
      { label: "Nov 30", opp: "por", ha: "@", res: "W", H1: 54, total: 123, oppScore: 115 },
      { label: "Nov 28", opp: "phx", ha: "vs", res: "W", H1: 56, total: 123, oppScore: 119 },
      { label: "Nov 26", opp: "min", ha: "vs", res: "W", H1: 49, total: 113, oppScore: 105 },
      { label: "Nov 23", opp: "por", ha: "vs", res: "W", H1: 67, total: 122, oppScore: 95 },
      { label: "Nov 21", opp: "utah", ha: "@", res: "W", H1: 67, total: 144, oppScore: 112 },
      { label: "Nov 19", opp: "sac", ha: "vs", res: "W", H1: 58, total: 113, oppScore: 99 },
    ],
  },
  sga: {
    id: "sga", type: "player", name: "Shai Gilgeous-Alexander", short: "SGA",
    abbr: "okc", league: "nba", pos: "Base", nextOpp: "den", nextHA: "vs", nextTime: "Hoy 21:00 hrs (Chile)",
    games: [
      { label: "Nov 08", opp: "sac", ha: "@", res: "W", score: "132-101", PTS: 30, REB: 1, AST: 3, TPM: 2 },
      { label: "Nov 06", opp: "por", ha: "@", res: "L", score: "121-119", PTS: 35, REB: 9, AST: 4, TPM: 3 },
      { label: "Nov 05", opp: "lac", ha: "@", res: "W", score: "126-107", PTS: 30, REB: 4, AST: 12, TPM: 4 },
      { label: "Nov 02", opp: "no", ha: "vs", res: "W", score: "137-106", PTS: 30, REB: 2, AST: 7, TPM: 1 },
      { label: "Oct 31", opp: "wsh", ha: "vs", res: "W", score: "127-108", PTS: 31, REB: 3, AST: 7, TPM: 3 },
      { label: "Oct 29", opp: "sac", ha: "vs", res: "W", score: "107-101", PTS: 31, REB: 9, AST: 4, TPM: 1 },
      { label: "Oct 28", opp: "dal", ha: "@", res: "W", score: "101-94", PTS: 23, REB: 4, AST: 8, TPM: 1 },
      { label: "Oct 25", opp: "atl", ha: "@", res: "W", score: "117-100", PTS: 30, REB: 5, AST: 5, TPM: 2 },
      { label: "Oct 23", opp: "ind", ha: "@", res: "W", score: "141-135", PTS: 55, REB: 8, AST: 5, TPM: 2 },
      { label: "Oct 21", opp: "hou", ha: "vs", res: "W", score: "125-124", PTS: 35, REB: 5, AST: 5, TPM: 1 },
    ],
  },
  nyl: {
    id: "nyl", type: "team", name: "New York Liberty", short: "Liberty",
    abbr: "ny", league: "wnba", nextOpp: "con", nextHA: "@", nextTime: "Hoy 20:00 hrs (Chile)",
    games: [
      { label: "Ago 12", opp: "la", ha: "@", res: "W", H1: 50, total: 105, oppScore: 97 },
      { label: "Ago 10", opp: "min", ha: "vs", res: "L", H1: 38, total: 71, oppScore: 83 },
      { label: "Ago 08", opp: "dal", ha: "@", res: "W", H1: 50, total: 88, oppScore: 77 },
      { label: "Ago 05", opp: "dal", ha: "vs", res: "W", H1: 37, total: 85, oppScore: 76 },
      { label: "Ago 03", opp: "con", ha: "@", res: "W", H1: 45, total: 87, oppScore: 78 },
      { label: "Ago 01", opp: "con", ha: "@", res: "L", H1: 37, total: 62, oppScore: 78 },
      { label: "Jul 30", opp: "min", ha: "@", res: "L", H1: 42, total: 93, oppScore: 100 },
      { label: "Jul 28", opp: "dal", ha: "@", res: "L", H1: 36, total: 82, oppScore: 92 },
      { label: "Jul 26", opp: "la", ha: "vs", res: "L", H1: 45, total: 99, oppScore: 101 },
      { label: "Jul 25", opp: "phx", ha: "vs", res: "W", H1: 46, total: 89, oppScore: 76 },
      { label: "Jul 22", opp: "ind", ha: "vs", res: "W", H1: 42, total: 98, oppScore: 84 },
    ],
  },
};

const MKTS = {
  team: [
    { id: "H1", label: "Total equipo 1er tiempo", get: (g) => g.H1 },
    { id: "H2", label: "Total equipo 2do tiempo", get: (g) => g.total - g.H1 },
    { id: "TT", label: "Total del equipo", get: (g) => g.total },
    { id: "FG", label: "Total del partido", get: (g) => g.total + g.oppScore },
  ],
  player: [
    { id: "PTS", label: "Puntos", get: (g) => g.PTS },
    { id: "REB", label: "Rebotes", get: (g) => g.REB },
    { id: "AST", label: "Asistencias", get: (g) => g.AST },
    { id: "TPM", label: "Triples (3PM)", get: (g) => g.TPM },
    { id: "PRA", label: "Pts+Reb+Ast", get: (g) => g.PTS + g.REB + g.AST },
  ],
};
const mktOf = (s, id) => MKTS[s.type].find((m) => m.id === id) || MKTS[s.type][0];

// Línea de la casa por sujeto/mercado (en producción viene de API-Sports)
const BOOK_LINES = {
  okc: { H1: 53.5, H2: 60.5, TT: 114.5, FG: 225.5 },
  sga: { PTS: 26.5, REB: 4.5, AST: 6.5, TPM: 1.5, PRA: 39.5 },
  nyl: { H1: 41.5, H2: 47.5, TT: 84.5, FG: 165.5 },
};

// Umbral de detección y de valor
const THRESHOLD = 70;     // % mínimo sobre la línea
const MIN_N = 6;          // mínimo de partidos en la ventana
const VALUE_GAP = 0.06;   // brecha mínima (6%) entre promedio reciente y línea = "Valor"

function detectOpps(league) {
  const out = [];
  Object.values(SUBJECTS).forEach((s) => {
    if (league && s.league !== league) return;
    MKTS[s.type].forEach((mk) => {
      const line = (BOOK_LINES[s.id] || {})[mk.id];
      if (line == null) return;
      const recent = s.games.slice(0, 10).map(mk.get);
      if (recent.length < MIN_N) return;
      const over = recent.filter((v) => v > line).length;
      const overPct = Math.round((over / recent.length) * 100);
      const dir = overPct >= 50 ? "over" : "under";
      const pct = dir === "over" ? overPct : 100 - overPct;
      if (pct < THRESHOLD) return;
      // concordancia con la temporada (todos los partidos disponibles)
      const seasonVals = s.games.map(mk.get);
      const seasonOverPct = Math.round((seasonVals.filter((v) => v > line).length / seasonVals.length) * 100);
      const seasonDir = seasonOverPct >= 50 ? "over" : "under";
      if (seasonDir !== dir) return; // L10 y temporada deben apuntar igual
      // brecha vs línea (capa de valor)
      const prom = avg(recent);
      const gap = dir === "over" ? prom - line : line - prom;
      const gapPct = gap / line;
      out.push({ sid: s.id, mkt: mk.id, dir, line, pct, hits: dir === "over" ? over : recent.length - over, n: recent.length, prom: Math.round(prom * 10) / 10, gap: Math.round(gap * 10) / 10, value: gapPct >= VALUE_GAP });
    });
  });
  // Valor primero, luego por %
  return out.sort((a, b) => (b.value - a.value) || (b.pct - a.pct));
}

// Partidos del día (demo) — los con datos abren detalle
const GAMES = [
  { league: "nba", home: "okc", away: "den", time: "21:00", sid: "okc", trends: 4 },
  { league: "nba", home: "bkn", away: "hou", time: "20:00", sid: "okc", trends: 3 },
  { league: "nba", home: "mia", away: "atl", time: "20:30", sid: "okc", trends: 2 },
  { league: "wnba", home: "con", away: "ny", time: "20:00", sid: "nyl", trends: 3 },
  { league: "wnba", home: "lv", away: "sea", time: "22:00", sid: "nyl", trends: 2 },
  { league: "wnba", home: "chi", away: "atl", time: "19:00", sid: "nyl", trends: 1 },
];

/* ---------- helpers ---------- */
const median = (a) => { if (!a.length) return 0; const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);
const windowGames = (g, w, opp) => w === "L5" ? g.slice(0, 5) : w === "L20" ? g.slice(0, 20) : w === "H2H" ? g.filter((x) => x.opp === opp) : g.slice(0, 10);

/* ---------- marca Edge Elite (SVG) ---------- */
function EEMark({ size = 30 }) {
  const gid = "eeg" + size;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden>
      <defs>
        <radialGradient id={gid} cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#FF8A4C" /><stop offset="55%" stopColor={ORANGE} /><stop offset="100%" stopColor={ORANGE_DK} />
        </radialGradient>
      </defs>
      <circle cx="50" cy="50" r="46" fill={`url(#${gid})`} />
      <path d="M50 4v92M4 50h92M18 18c20 16 44 16 64 0M18 82c20-16 44-16 64 0" stroke="#fff" strokeWidth="3.5" fill="none" opacity="0.85" />
    </svg>
  );
}
// Wrapper que usa el logo oficial (importado de ../EEBrand). El componente
// oficial usa `color: currentColor`, así que el wrapper le pasa textColor
// vía `style.color` para respetar el tema dark/light.
function EELogo({ textColor = "#1D1D1F" }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", color: textColor }}>
      <OfficialEELogo size={22} />
    </div>
  );
}

const ESPN_LOGO_BASE = "https://a.espncdn.com/i/teamlogos";
// URL del logo oficial ESPN por abreviatura/liga. Si la imagen 404, el
// componente <Logo> hace fallback automático a <Mono> via onError.
function logoUrl(league, abbr) {
  if (!abbr) return null;
  const lg = (league || "nba").toLowerCase();
  return ESPN_LOGO_BASE + "/" + lg + "/500/" + abbr.toLowerCase() + ".png";
}
const LOGOS = new Proxy({}, {
  get(_, key) {
    // back-compat: LOGOS["nba/okc"] → URL
    const idx = String(key).indexOf("/");
    if (idx < 0) return null;
    return logoUrl(String(key).slice(0, idx), String(key).slice(idx + 1));
  }
});
/* ---------- logo de equipo con fallback ---------- */
function Mono({ abbr, size = 26 }) {
  const pal = ["#FF6B2C", "#1E40AF", "#16A34A", "#BA3600", "#0F766E", "#7C3AED"];
  const hue = pal[((abbr || "?").charCodeAt(0)) % pal.length];
  return <div style={{ width: size, height: size, borderRadius: "50%", background: hue, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.34, fontWeight: 800, flexShrink: 0 }}>{(abbr || "?").toUpperCase().slice(0, 3)}</div>;
}
function Logo({ abbr, league = "nba", size = 26 }) {
  const [failed, setFailed] = useState(false);
  const src = abbr ? logoUrl(league, abbr) : null;
  if (!abbr || !src || failed) return <Mono abbr={abbr} size={size} />;
  return (
    <img
      alt={abbr}
      width={size}
      height={size}
      src={src}
      onError={() => setFailed(true)}
      style={{ objectFit: "contain", flexShrink: 0 }}
    />
  );
}

/* foto del jugador (headshot ESPN) con fallback a iniciales/monograma */
function PlayerPhoto({ playerId, league = "nba", initials, size = 36 }) {
  const [failed, setFailed] = useState(false);
  const src = playerId
    ? `https://a.espncdn.com/i/headshots/${(league || "nba").toLowerCase()}/players/full/${playerId}.png`
    : null;
  if (!src || failed) {
    return (
      <div
        style={{
          width: size, height: size, borderRadius: "50%",
          background: "#1E40AF", color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: size * 0.35, fontWeight: 700, flexShrink: 0,
        }}
      >
        {(initials || "?").toUpperCase().slice(0, 2)}
      </div>
    );
  }
  return (
    <img
      alt={initials || "player"}
      width={size}
      height={size}
      src={src}
      onError={() => setFailed(true)}
      style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", flexShrink: 0,
        // transparente: si la imagen tiene fondo propio (ESPN headshots
        // suelen traer fondo), se respeta. Si no, hereda del contenedor.
        background: "transparent",
      }}
    />
  );
}

/* etiqueta de valor: número dentro de un círculo en la base de la barra */
function ValueBubble({ x, y, width, height, value }) {
  const r = Math.min(12, width / 2 - 1);
  if (r < 5) return null;
  const cx = x + width / 2;
  const cy = Math.max(y + r + 1, y + height - r - 4);
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill="rgba(0,0,0,0.30)" stroke="rgba(255,255,255,0.55)" strokeWidth={1} />
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={r >= 10 ? 10.5 : 8.5} fontWeight={800} fill="#fff">{value}</text>
    </g>
  );
}

/* tick del eje X: @/vs + logo del rival */
function OppTick({ x, y, payload, data, league, muted = "#6E6E73", fallback = "#1E40AF" }) {
  const d = data[payload.value]; if (!d) return null;
  const src = LOGOS[`${league}/${(d.opp || "").toLowerCase()}`];
  const sz = data.length > 11 ? 18 : 22;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={11} textAnchor="middle" fontSize={10} fontWeight={700} fill={muted}>{d.ha}</text>
      {src
        ? <image href={src} x={-sz / 2} y={15} width={sz} height={sz} />
        : <circle cx={0} cy={15 + sz / 2} r={sz / 2} fill={fallback} />}
    </g>
  );
}

const FONT = "'Inter Tight', -apple-system, system-ui, sans-serif";

// Etiquetas legibles para los markets que escribe el backend
const MARKET_LABELS = {
  total_game: "Total del partido",
  home_total_game: "Total del equipo (local)",
  away_total_game: "Total del equipo (visitante)",
  total_1H: "Total partido · 1er tiempo",
  total_2H: "Total partido · 2do tiempo",
  total_Q1: "Total partido · Q1",
  total_Q2: "Total partido · Q2",
  total_Q3: "Total partido · Q3",
  total_Q4: "Total partido · Q4",
  home_total_1H: "Total equipo · 1er tiempo",
  away_total_1H: "Total equipo · 1er tiempo",
  home_total_2H: "Total equipo · 2do tiempo",
  away_total_2H: "Total equipo · 2do tiempo",
  home_total_Q1: "Total equipo · Q1",
  away_total_Q1: "Total equipo · Q1",
  home_total_Q2: "Total equipo · Q2",
  away_total_Q2: "Total equipo · Q2",
  home_total_Q3: "Total equipo · Q3",
  away_total_Q3: "Total equipo · Q3",
  home_total_Q4: "Total equipo · Q4",
  away_total_Q4: "Total equipo · Q4",
  player_points: "Puntos",
  player_assists: "Asistencias",
  player_triples: "Triples (3PM)",
  player_threes: "Triples (3PM)",
  player_pra: "Pts+Reb+Ast",
  player_pa: "Pts+Ast",
};

// Convierte un market key del backend al id interno de MKTS (frontend).
// Si no encuentra match, devuelve null y el Detail usa el default del MKTS.
function backendMktToFrontend(backendMkt, subjectKind) {
  if (!backendMkt) return null;
  if (subjectKind === "team") {
    if (backendMkt === "total_game") return "FG";
    if (backendMkt === "home_total_game" || backendMkt === "away_total_game") return "TT";
    if (backendMkt === "home_total_1H" || backendMkt === "away_total_1H") return "H1";
    if (backendMkt === "home_total_2H" || backendMkt === "away_total_2H") return "H2";
  }
  if (subjectKind === "player") {
    if (backendMkt === "player_points") return "PTS";
    if (backendMkt === "player_assists") return "AST";
    if (backendMkt === "player_triples" || backendMkt === "player_threes") return "TPM";
    if (backendMkt === "player_pra") return "PRA";
  }
  return null;
}

// Date label corto en español para el header
function dateLabelEs(date) {
  if (!date) return "";
  try {
    const d = new Date(date + "T00:00:00");
    const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
  } catch (_) { return ""; }
}

/* ============================ HUB / PRINCIPAL ============================ */
function Hub({ games = [], opps = [], loading, onOpen, onOpenGame, theme, toggleTheme }) {
  const C = THEMES[theme];
  const [league, setLeague] = useState("nba");

  // Filtrar por liga seleccionada
  const filteredGames = useMemo(
    () => (games || []).filter((g) => g.league === league),
    [games, league]
  );
  const detected = useMemo(
    () => (opps || []).filter((o) => o.league === league && o.is_opportunity),
    [opps, league]
  );

  const today = (games && games[0] && games[0].date_utc)
    ? games[0].date_utc.slice(0, 10)
    : new Date().toISOString().slice(0, 10);
  return (
    <div className="ee-wrap" style={{ fontFamily: FONT, background: C.bg, color: C.text, minHeight: "100%" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700;800;900&display=swap');
        /* Mobile: 1 columna full width con padding propio */
        .ee-wrap{width:100%;margin:0 auto;padding:16px 14px 28px;box-sizing:border-box;}
        .ee-detail{width:100%;max-width:720px;margin:0 auto;padding:14px 14px 26px;box-sizing:border-box;}
        .ee-title{font-size:22px;font-weight:800;letter-spacing:-0.02em;margin:2px 0 2px;}
        /* Flex-wrap: cada card flex: 1 1 320px (min 320, crece hasta su max).
           Con max-width por item, 1 sola card queda en 480px (no full width). */
        .ee-grid{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:22px;}
        .ee-grid > *{flex:1 1 320px;max-width:480px;min-width:0;}
        .ee-games{display:flex;flex-wrap:wrap;gap:10px;}
        .ee-games > *{flex:1 1 280px;max-width:420px;min-width:0;}
        .ee-span{flex-basis:100% !important;max-width:none !important;}
        @media(min-width:760px){
          /* Embebido en dashboard: ocupar TODO el ancho disponible del main-content */
          .ee-wrap{max-width:none;padding:26px 28px 44px;}
          .ee-detail{max-width:720px;padding:24px 28px 40px;}
          .ee-title{font-size:26px;}
        }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <EELogo textColor={C.text} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 600 }}>{dateLabelEs(today)}</span>
        </div>
      </div>

      <h1 className="ee-title">Oportunidades NBA y WNBA</h1>
      <p style={{ fontSize: 13.5, color: C.muted, margin: "0 0 16px", lineHeight: 1.4 }}>Cuando superan la línea en 70%+ de sus últimos partidos</p>

      {/* tabs liga */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", background: C.seg, borderRadius: 12, padding: 3, marginBottom: 18, width: "fit-content" }}>
        {[["nba", "NBA"], ["wnba", "WNBA"]].map(([id, lb]) => (
          <button key={id} onClick={() => setLeague(id)} style={{ border: "none", cursor: "pointer", borderRadius: 9, padding: "8px 22px", fontSize: 13.5, fontWeight: 700, fontFamily: FONT, background: league === id ? C.orange : "transparent", color: league === id ? "#fff" : C.muted, boxShadow: league === id ? "0 1px 4px rgba(255,107,44,.35)" : "none" }}>{lb}</button>
        ))}
      </div>

      {/* PARTIDOS DEL DÍA — arriba (entry point principal) */}
      <h2 style={{ fontSize: 15, fontWeight: 800, margin: "0 0 10px", color: C.text }}>Partidos de hoy</h2>
      <div className="ee-games" style={{ marginBottom: 22 }}>
        {filteredGames.length === 0 && (
          <div className="ee-span" style={{ border: `1px dashed ${C.line}`, borderRadius: 16, background: C.card, padding: "22px 16px", textAlign: "center", flexBasis: "100%", maxWidth: "none" }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>
              {loading ? "Cargando partidos del día…" : `No hay partidos de ${league.toUpperCase()} hoy.`}
            </div>
          </div>
        )}
        {filteredGames.map((g, i) => {
          const awayAbbr = (g.away_abbr || "").toLowerCase();
          const homeAbbr = (g.home_abbr || "").toLowerCase();
          // Display name limpio (sin sufijo " W" para WNBA, ya lo dice la liga)
          const cleanName = (n) => (n || "").replace(/\s+W$/, "");
          return (
            <button
              key={g.id || i}
              onClick={() => onOpenGame(g)}
              style={{ cursor: "pointer", border: `1px solid ${C.line}`, borderRadius: 16, background: C.card, color: C.text, padding: "13px 15px", display: "flex", alignItems: "center", gap: 12, fontFamily: FONT, boxShadow: "0 2px 10px rgba(0,0,0,.04)" }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <Logo abbr={awayAbbr} league={g.league} size={30} />
                <Logo abbr={homeAbbr} league={g.league} size={30} />
              </div>
              <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {(awayAbbr || cleanName(g.away)).toUpperCase()} <span style={{ color: C.muted, fontWeight: 600 }}>@</span> {(homeAbbr || cleanName(g.home)).toUpperCase()}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
                  {g.time_cl || ""} · {g.league.toUpperCase()}
                </div>
              </div>
              {g.trends > 0 && (
                <span style={{ fontSize: 11, fontWeight: 800, color: C.orange, background: `${C.orange}14`, padding: "5px 9px", borderRadius: 999 }}>
                  {g.trends} tendencia{g.trends === 1 ? "" : "s"}
                </span>
              )}
              <span style={{ color: C.muted, fontSize: 18 }}>›</span>
            </button>
          );
        })}
      </div>

      {/* OPORTUNIDADES DETECTADAS — abajo */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.orange, boxShadow: `0 0 0 4px ${C.orange}22` }} />
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: C.text }}>Detectadas hoy</h2>
      </div>
      <div className="ee-grid" style={{ position: "relative", zIndex: 1 }}>
        {detected.length === 0 && (
          <div className="ee-span" style={{ border: `1px dashed ${C.line}`, borderRadius: 18, background: C.card, padding: "26px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4, color: C.text }}>
              {loading ? "Cargando oportunidades…" : "Sin oportunidades sobre el umbral hoy"}
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, fontWeight: 600 }}>
              {loading ? "Buscando tendencias para los partidos de hoy." : `Ninguna tendencia de ${league.toUpperCase()} superó el 70% en sus últimos partidos.`}
            </div>
          </div>
        )}
        {detected.map((o, i) => {
          // Schema backend
          const dirLower = (o.direction || "").toLowerCase() === "over" ? "over" : "under";
          const pctInt = Math.round(o.pct || 0);
          const n = o.n || 0;
          const hits = Math.round((pctInt * n) / 100);
          const subjectAbbr = (o.subject_abbr || "").toLowerCase();
          const oppAbbr = (o.next_opp_abbr || "").toLowerCase();
          const subjectKind = o.subject_kind || "team";
          const mkLabel = MARKET_LABELS[o.market] || o.market;
          const vals = (o.recent_values || []).slice(0, n);
          const mx = vals.length ? Math.max(...vals) : 1;
          const promF = (o.avg_recent || 0).toFixed(1);
          const gapF = o.is_value ? `+${Math.abs((o.deviation || 0) * 100).toFixed(1)}%` : "";

          return (
            <button
              key={i}
              onClick={() => onOpen(o.subject_id, backendMktToFrontend(o.market, subjectKind), dirLower, o.line)}
              style={{ width: "100%", textAlign: "left", cursor: "pointer", border: `1px solid ${C.line}`, borderRadius: 18, background: C.card, color: C.text, padding: 16, fontFamily: FONT, boxShadow: "0 4px 18px rgba(0,0,0,.05)" }}
            >
              {/* fila superior: sujeto + partido + hora Chile */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <Logo abbr={subjectAbbr} league={o.league} size={36} />
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 15, fontWeight: 800, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{o.subject_name}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600, display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                    {oppAbbr && <Logo abbr={oppAbbr} league={o.league} size={14} />}
                    {oppAbbr.toUpperCase()} {o.next_ha === "vs" ? "@" : "vs"} {subjectAbbr.toUpperCase()}
                    {o.next_time ? ` · ${o.next_time}` : ""}
                  </div>
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 800, color: subjectKind === "team" ? C.blue : C.orange, background: (subjectKind === "team" ? C.blue : C.orange) + "14", padding: "4px 9px", borderRadius: 999 }}>
                  {subjectKind === "team" ? "Equipo" : "Jugador"}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 10px" }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>
                  {mkLabel}{" "}
                  <span style={{ color: dirLower === "over" ? C.pos : C.blue }}>
                    {dirLower === "over" ? "Over" : "Under"} {o.line}
                  </span>
                  <span style={{ color: C.muted, fontWeight: 600 }}> · línea casa</span>
                </span>
                {o.is_value && <span style={{ fontSize: 10.5, fontWeight: 800, color: "#fff", background: `linear-gradient(90deg, ${C.orange}, ${C.orangeDark})`, padding: "3px 8px", borderRadius: 999 }}>🔥 Valor</span>}
              </div>

              {/* sparkline ancho — desde recent_values del backend */}
              {vals.length > 0 && (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 46, marginBottom: 12 }}>
                  {vals.slice().reverse().map((v, j) => {
                    const h = dirLower === "over" ? v > o.line : v < o.line;
                    return <div key={j} style={{ flex: 1, height: `${Math.max((v / mx) * 100, 14)}%`, borderRadius: "4px 4px 2px 2px", background: h ? `linear-gradient(180deg,#22C55E,${C.posDk})` : C.track }} />;
                  })}
                </div>
              )}

              {/* fila inferior: % + brecha + ver */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: C.pos, letterSpacing: "-0.03em" }}>{pctInt}%</span>
                <span style={{ fontSize: 12.5, color: C.muted, fontWeight: 600, marginLeft: 8 }}>{hits}/{n} últimos</span>
                {o.is_value && <span style={{ fontSize: 11.5, color: C.orangeDark, fontWeight: 700, marginLeft: 10 }}>prom {promF} · {gapF} vs línea</span>}
                <span style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 800, color: C.orange }}>Ver tendencia ›</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================ DETALLE / WIDGET ============================ */
const WINDOWS = [["H2H", "H2H"], ["L5", "L5"], ["L10", "L10"], ["L20", "L20"], ["S2526", "25/26"]];

function Detail(props) {
  // Carga el subject desde Firestore (lazy por id) y lo adapta al schema
  // que espera DetailInner (mismo del componente original con embebidos).
  // Mantiene estados de UI internos al inner para no re-resetear al rehidratar.
  const { subjectId, theme, onBack } = props;
  const { subject, loading } = useTrendSubject(subjectId);
  const adapted = adaptSubjectFromBackend(subject);
  const C = THEMES[theme];

  if (loading || !adapted) {
    return (
      <div className="ee-detail" style={{ fontFamily: FONT, background: C.bg, color: C.text, minHeight: "100%" }}>
        <style>{`
          .ee-detail{width:100%;margin:0 auto;padding:14px 14px 26px;box-sizing:border-box;}
          @media(min-width:760px){ .ee-detail{max-width:600px;padding:24px 24px 36px;} }
        `}</style>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button onClick={onBack} style={{ border: "none", background: "none", cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>‹ Oportunidades</button>
          <EELogo textColor={C.text} />
        </div>
        <div style={{ padding: "40px 16px", textAlign: "center", color: C.muted, fontSize: 14 }}>
          {loading ? "Cargando tendencias…" : "No se encontraron datos para este sujeto."}
        </div>
      </div>
    );
  }
  return <DetailInner {...props} s={adapted} />;
}

function DetailInner({ s, initMkt, initDir, initLine, onBack, theme, toggleTheme }) {
  const C = THEMES[theme];
  const [win, setWin] = useState("L10");
  const [dir, setDir] = useState(initDir || "over");
  const [mktId, setMktId] = useState((initMkt && MKTS[s.type].some((m) => m.id === initMkt)) ? initMkt : MKTS[s.type][0].id);
  const mkt = mktOf(s, mktId);

  const wGames = useMemo(() => windowGames(s.games, win, s.nextOpp), [s, win]);
  const values = wGames.map(mkt.get);
  const defLine = (initLine && mktId === initMkt) ? initLine : Math.round(median(values)) + 0.5;
  const [lineInput, setLineInput] = useState(null);
  const line = lineInput == null ? defLine : lineInput;

  const isHit = (v) => dir === "over" ? v > line : v < line;
  const hits = values.filter(isHit).length;
  const pct = values.length ? Math.round((hits / values.length) * 100) : 0;
  const pillPct = (w) => { const g = windowGames(s.games, w, s.nextOpp).map(mkt.get); if (!g.length) return null; return Math.round((g.filter(isHit).length / g.length) * 100); };

  const scaleMax = Math.max(...values, line) * 1.14 || 1;
  const display = [...wGames].reverse();
  const dispVals = display.map(mkt.get);
  const chartData = display.map((g, i) => ({ idx: i, value: dispVals[i], hit: isHit(dispVals[i]), opp: g.opp, ha: g.ha }));
  const headerLine = `${mkt.label} ${dir === "over" ? "Over" : "Under"} ${line}`;

  return (
    <div className="ee-detail" style={{ fontFamily: FONT, background: C.bg, color: C.text, minHeight: "100%" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700;800;900&display=swap');
        @keyframes grow { from { transform: scaleY(0);} to { transform: scaleY(1);} }
        .ee-detail{max-width:480px;margin:0 auto;padding:14px 14px 26px;}
        @media(min-width:760px){ .ee-detail{max-width:600px;padding:24px 24px 36px;} }`}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <button onClick={onBack} style={{ border: "none", background: "none", cursor: "pointer", fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.muted, display: "flex", alignItems: "center", gap: 6 }}>‹ Oportunidades</button>
        <EELogo textColor={C.text} />
      </div>

      {/* TARJETA */}
      <div style={{ position: "relative", background: C.card, color: C.text, borderRadius: 22, padding: "20px 18px 8px", boxShadow: "0 8px 30px rgba(0,0,0,.07)", overflow: "hidden" }}>
        {/* marca de agua */}
        <div style={{ position: "absolute", top: -28, right: -26, opacity: 0.06, transform: "rotate(-8deg)", pointerEvents: "none" }}><EEMark size={190} /></div>

        {/* header */}
        <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, color: C.muted, fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <Logo abbr={s.nextOpp} league={s.league} size={16} />
              {s.nextOpp.toUpperCase()} {s.nextHA === "vs" ? "@" : "vs"} {s.abbr.toUpperCase()} · {s.nextTime}
            </div>
            <div style={{ fontSize: 23, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.08 }}>{s.name}</div>
            <div style={{ fontSize: 17, color: C.muted, fontWeight: 600, marginTop: 3 }}>{headerLine}</div>
          </div>
          <div style={{ width: 74, height: 74, borderRadius: "50%", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Logo abbr={s.abbr} league={s.league} size={58} />
          </div>
        </div>

        {/* controles */}
        <div style={{ display: "flex", gap: 8, margin: "16px 0 4px", flexWrap: "wrap", position: "relative" }}>
          <div style={{ display: "flex", background: C.seg, borderRadius: 999, padding: 3 }}>
            {[["over", "Over", C.pos], ["under", "Under", C.blue]].map(([id, lb, col]) => (
              <button key={id} onClick={() => setDir(id)} style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "6px 16px", fontSize: 13, fontWeight: 700, fontFamily: FONT, background: dir === id ? col : "transparent", color: dir === id ? "#fff" : C.muted }}>{lb}</button>
            ))}
          </div>
          <select value={mktId} onChange={(e) => { setMktId(e.target.value); setLineInput(null); }} style={{ border: `1px solid ${C.line}`, borderRadius: 999, padding: "6px 12px", fontSize: 13, fontWeight: 700, color: C.text, fontFamily: FONT, background: C.card, cursor: "pointer" }}>
            {MKTS[s.type].map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
              Línea
              {initLine && mktId === initMkt && lineInput == null ? (
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: C.orange, background: `${C.orange}14`, padding: "2px 6px", borderRadius: 999 }}>
                  casa
                </span>
              ) : null}
            </span>
            <input type="number" step="0.5" value={line} onChange={(e) => setLineInput(parseFloat(e.target.value))} style={{ width: 62, border: `1px solid ${C.line}`, borderRadius: 8, padding: "6px 8px", fontSize: 13, fontWeight: 800, textAlign: "center", fontFamily: FONT, color: C.text, background: C.card }} />
          </div>
        </div>

        {/* pills ventanas */}
        <div style={{ display: "flex", gap: 6, margin: "14px 0", overflowX: "auto", paddingBottom: 2, position: "relative" }}>
          {WINDOWS.map(([id, lb]) => {
            const p = pillPct(id);
            return (
              <div key={id} style={{ textAlign: "center", flexShrink: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: p == null ? "#CFCFD4" : p >= 50 ? C.pos : C.neg, marginBottom: 4, height: 13 }}>{p == null ? "—" : `${p}%`}</div>
                <button onClick={() => setWin(id)} style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, fontFamily: FONT, background: win === id ? C.chip : C.seg, color: win === id ? "#fff" : C.muted }}>{lb}</button>
              </div>
            );
          })}
        </div>

        {/* split + resumen */}
        <div style={{ height: 5, borderRadius: 5, display: "flex", overflow: "hidden", margin: "4px 0 14px", position: "relative" }}>
          <div style={{ flex: hits || 0.001, background: C.pos }} /><div style={{ flex: (values.length - hits) || 0.001, background: C.track }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, position: "relative" }}>
          <div><span style={{ fontSize: 32, fontWeight: 900, letterSpacing: "-0.03em" }}>{pct}%</span><span style={{ fontSize: 13, color: C.muted, fontWeight: 600, marginLeft: 8 }}>{hits}/{values.length} partidos</span></div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>Prom <b style={{ color: C.text }}>{avg(values).toFixed(1)}</b> <span style={{ margin: "0 5px", color: C.line }}>·</span> Med <b style={{ color: C.text }}>{median(values)}</b></div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 6, background: `${C.orange}12`, padding: "3px 9px", borderRadius: 999 }}>
              <EEMark size={13} />
              <span style={{ fontSize: 11, fontWeight: 800, color: C.orangeDark, letterSpacing: "0.01em" }}>www.edgeelite.cl</span>
            </div>
          </div>
        </div>

        {/* GRÁFICO (Recharts) */}
        <div style={{ position: "relative", marginTop: 6 }}>
          {/* marca horizontal en la zona alta del gráfico (sobre las barras) */}
          <div aria-hidden style={{ position: "absolute", top: 12, left: 0, right: 0, zIndex: 5, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
            <span style={{ fontSize: 19, fontWeight: 900, letterSpacing: "0.04em", color: C.text, opacity: 0.22, whiteSpace: "nowrap", textShadow: C.wmShadow }}>www.edgeelite.cl</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 6, right: 14, left: 0, bottom: 0 }} barCategoryGap={display.length > 11 ? "2%" : "4%"}>
              <defs>
                <linearGradient id="gPos" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22C55E" /><stop offset="100%" stopColor={C.posDk} /></linearGradient>
                <linearGradient id="gNeg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F87171" /><stop offset="100%" stopColor={C.negDk} /></linearGradient>
              </defs>
              <YAxis hide domain={[0, scaleMax]} />
              <XAxis dataKey="idx" interval={0} tickLine={false} axisLine={false} height={40} tick={<OppTick data={chartData} league={s.league} muted={C.muted} fallback={C.blue} />} />
              <Bar dataKey="value" radius={[7, 7, 3, 3]} isAnimationActive animationDuration={550}>
                {chartData.map((d, i) => <Cell key={i} fill={d.hit ? "url(#gPos)" : "url(#gNeg)"} />)}
                <LabelList dataKey="value" content={<ValueBubble />} />
              </Bar>
              {/* línea de la casa SIEMPRE encima de las barras */}
              <ReferenceLine y={line} stroke={C.text} strokeWidth={2.5} strokeDasharray="6 4" ifOverflow="extendDomain" isFront
                label={({ viewBox }) => (
                  <g transform={`translate(${viewBox.x + viewBox.width},${viewBox.y})`}>
                    <rect x={-44} y={-11} width={44} height={22} rx={7} fill={C.chip} />
                    <text x={-22} y={4} textAnchor="middle" fill="#fff" fontSize={11.5} fontWeight={800}>{line}</text>
                  </g>
                )} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* recientes */}
        <div style={{ marginTop: 18, borderTop: `1px solid ${C.line}`, paddingTop: 4, position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 2 }}>Final</div>
          {s.games.slice(0, 6).map((g, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", padding: "9px 0", borderBottom: i < 5 ? `1px solid ${C.bg}` : "none" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: g.res === "W" ? C.pos : C.neg, marginRight: 10 }} />
              <span style={{ fontSize: 13, color: C.muted, fontWeight: 600, width: 52 }}>{g.label}</span>
              <Logo abbr={s.abbr} league={s.league} size={20} /><Logo abbr={g.opp} league={s.league} size={20} />
              <span style={{ fontSize: 13.5, fontWeight: 700, marginLeft: 10 }}>{g.ha === "vs" ? "vs" : "@"} {g.opp.toUpperCase()}</span>
              <span style={{ marginLeft: "auto", fontSize: 13.5, fontWeight: 800 }}>{g.type === "team" ? `${g.total}-${g.oppScore}` : g.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================ NIVEL 2 · VISTA DE PARTIDO ============================ */
/* Pantalla intermedia entre el Hub (Partidos de hoy) y el Detalle.
   Lista los 2 equipos lado a lado (apilados <760px) con sus jugadores
   trackeados como ítems tocables. Tap → Nivel 3 (Detail) con mercado default. */
function GameView({ game, opps = [], onSelectSubject, onBack, theme }) {
  const C = THEMES[theme];

  // Data del backend: game.home_abbr, game.away_abbr, game.home_players[], game.away_players[]
  // Cada player: { id (subject_id), name, espn_id, pos }
  // El team subject ID se construye igual que en el backend: `${league}_team_${abbr}`
  const homeAbbr = (game.home_abbr || "").toLowerCase();
  const awayAbbr = (game.away_abbr || "").toLowerCase();
  const homeTeamId = homeAbbr ? `${game.league}_team_${homeAbbr}` : null;
  const awayTeamId = awayAbbr ? `${game.league}_team_${awayAbbr}` : null;

  // Display names limpios (sin sufijo " W" para WNBA)
  const cleanName = (n) => (n || "").replace(/\s+W$/, "");

  const sides = [
    {
      tag: "away",
      abbr: awayAbbr,
      teamId: awayTeamId,
      teamName: cleanName(game.away) || awayAbbr.toUpperCase(),
      players: game.away_players || [],
    },
    {
      tag: "home",
      abbr: homeAbbr,
      teamId: homeTeamId,
      teamName: cleanName(game.home) || homeAbbr.toUpperCase(),
      players: game.home_players || [],
    },
  ];

  const TeamCol = ({ side }) => {
    const { abbr, teamId, teamName, players } = side;
    return (
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 18, padding: 14, fontFamily: FONT }}>
        {/* header equipo */}
        <button
          onClick={() => teamId && onSelectSubject(teamId)}
          disabled={!teamId}
          style={{
            width: "100%", textAlign: "left", border: "none", background: "transparent",
            cursor: teamId ? "pointer" : "default", padding: 0, marginBottom: 10,
            display: "flex", alignItems: "center", gap: 12,
            opacity: teamId ? 1 : 0.55,
          }}
        >
          <Logo abbr={abbr} league={game.league} size={44} />
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
              {teamName}
            </div>
            <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 600 }}>
              {side.tag === "away" ? "Visitante" : "Local"} · {(abbr || "").toUpperCase()}
            </div>
          </div>
          {teamId && <span style={{ color: C.orange, fontSize: 18, fontWeight: 700 }}>›</span>}
        </button>

        {/* línea separadora */}
        <div style={{ height: 1, background: C.line, margin: "4px 0 8px" }} />

        {/* jugadores */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
          Jugadores trackeados
        </div>
        {players.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted, padding: "8px 4px", fontStyle: "italic" }}>
            Aún no hay jugadores trackeados para este equipo.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {players.map((p) => {
              const initials = (p.name || "")
                .split(" ")
                .map((w) => w[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("");
              return (
                <button
                  key={p.id}
                  onClick={() => onSelectSubject(p.id)}
                  style={{
                    width: "100%", textAlign: "left", border: `1px solid ${C.line}`,
                    background: C.bg, color: C.text, cursor: "pointer", borderRadius: 12, padding: 10,
                    display: "flex", alignItems: "center", gap: 10, fontFamily: FONT,
                  }}
                >
                  <PlayerPhoto playerId={p.espn_id || p.espnId} league={game.league} initials={initials} size={34} />
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: C.text, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                      {p.name}
                    </div>
                    {p.pos && (
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{p.pos}</div>
                    )}
                  </div>
                  <span style={{ color: C.orange, fontSize: 16, fontWeight: 700 }}>›</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ee-game" style={{ fontFamily: FONT, background: C.bg, color: C.text, minHeight: "100%" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700;800;900&display=swap');
        .ee-game{width:100%;margin:0 auto;padding:14px 14px 28px;box-sizing:border-box;}
        .ee-game-grid{display:grid;grid-template-columns:1fr;gap:12px;}
        @media(min-width:640px){
          .ee-game-grid{grid-template-columns:repeat(auto-fit, minmax(360px, 1fr));}
        }
        @media(min-width:760px){
          /* Embebido en dashboard: ocupar todo el ancho disponible */
          .ee-game{max-width:none;padding:24px 28px 40px;}
        }
        @media(min-width:1080px){ .ee-game-grid{max-width:1080px;margin:0 auto;gap:16px;} }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <button
          onClick={onBack}
          style={{
            border: "none", background: "none", cursor: "pointer", fontFamily: FONT,
            fontSize: 14, fontWeight: 700, color: C.muted,
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          ‹ Oportunidades
        </button>
        <EELogo textColor={C.text} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, color: C.muted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
          {game.league.toUpperCase()} · {game.time} hrs (Chile)
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", margin: 0 }}>
          {game.away.toUpperCase()} <span style={{ color: C.muted, fontWeight: 600 }}>@</span> {game.home.toUpperCase()}
        </h1>
        <p style={{ fontSize: 13, color: C.muted, margin: "4px 0 0", lineHeight: 1.4 }}>
          Toca un equipo o jugador para ver sus tendencias.
        </p>
      </div>

      <div className="ee-game-grid">
        {sides.map((s) => (
          <TeamCol key={s.tag} side={s} />
        ))}
      </div>
    </div>
  );
}

/* ============================ ROOT ============================ */
/* themeOverride: si el wrapper externo (Oportunidades.jsx) le pasa el tema
   heredado del dashboard ("light" | "dark"), usamos ese y NO mostramos
   toggle propio. Si no se pasa, el componente se autogestiona.

   La data viene de Firestore vía useTrendsToday():
   - trends_games/{today}  → partidos del día
   - trends_opportunities/{today} → oportunidades detectadas
   - trends_subjects/{id}  → solo cuando se abre el Detail (lazy)
*/
export default function EdgeEliteTrends({ themeOverride } = {}) {
  const [view, setView] = useState({ page: "hub" });
  const [internalTheme, setInternalTheme] = useState("light");
  const theme = themeOverride || internalTheme;
  const toggleTheme = themeOverride
    ? undefined
    : () => setInternalTheme((t) => (t === "light" ? "dark" : "light"));

  const { games, opps, loading } = useTrendsToday();

  if (view.page === "detail") {
    return (
      <Detail
        subjectId={view.sid}
        initMkt={view.mkt}
        initDir={view.dir}
        initLine={view.line}
        onBack={() => setView(view.fromGame ? { page: "game", game: view.fromGame } : { page: "hub" })}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    );
  }
  if (view.page === "game") {
    return (
      <GameView
        game={view.game}
        opps={opps}
        onSelectSubject={(sid) =>
          setView({ page: "detail", sid, fromGame: view.game })
        }
        onBack={() => setView({ page: "hub" })}
        theme={theme}
      />
    );
  }
  return (
    <Hub
      games={games}
      opps={opps}
      loading={loading}
      onOpen={(sid, mkt, dir, line) =>
        setView({ page: "detail", sid, mkt, dir, line })
      }
      onOpenGame={(game) => setView({ page: "game", game })}
      theme={theme}
      toggleTheme={toggleTheme}
    />
  );
}
